import { computed, inject, provide, ref, shallowRef, type ComputedRef, type InjectionKey, type Ref } from 'vue'
import type { TableBindingLinkBody } from './useTableBindingMutations'

/** Вид отложенной операции над строкой LIST-таблицы. */
export type PendingBindingKind = 'link' | 'unlink'

/** Одна отложенная операция: лежит в очереди до нажатия «Сохранить». */
export type PendingBindingOp = {
  key: string
  kind: PendingBindingKind
  list_group_id: number
  table_field_id: string
  child_object_list_id: number
  /** Отображаемое имя ребёнка (для строк «новая» в оверлее таблицы). */
  name?: string
}

/** Тело `table_bindings` PUT-запроса сохранения паспорта. */
export type TableBindingsDelta = {
  link: TableBindingLinkBody[]
  unlink: TableBindingLinkBody[]
}

/** Отложенные операции, привязанные к одной LIST-таблице (для оверлея строк). */
export type PendingForTable = {
  added: PendingBindingOp[]
  removed: PendingBindingOp[]
}

export type PendingTableBindings = {
  ops: Readonly<Ref<PendingBindingOp[]>>
  hasPending: ComputedRef<boolean>
  stageLink: (op: Omit<PendingBindingOp, 'key' | 'kind'>) => void
  stageUnlink: (op: Omit<PendingBindingOp, 'key' | 'kind'>) => void
  undo: (key: string) => void
  clear: () => void
  pendingForTable: (listGroupId: number, tableFieldId: string) => PendingForTable
  buildDelta: () => TableBindingsDelta | null
}

export const PENDING_BINDINGS_KEY: InjectionKey<PendingTableBindings> = Symbol('pendingTableBindings')

function opKey(kind: PendingBindingKind, listGroupId: number, tableFieldId: string, childId: number): string {
  return `${kind}:${listGroupId}:${tableFieldId}:${childId}`
}

function toBody(op: PendingBindingOp): TableBindingLinkBody {
  return {
    list_group_id: op.list_group_id,
    child_object_list_id: op.child_object_list_id,
    table_field_id: op.table_field_id,
  }
}

/**
 * Создаёт очередь отложенных link/unlink операций для паспортных LIST-таблиц.
 * Операции накапливаются локально и применяются единым блоком при «Сохранить».
 */
export function usePendingTableBindings(): PendingTableBindings {
  const ops = ref<PendingBindingOp[]>([])

  const hasPending = computed(() => ops.value.length > 0)

  function findIndex(kind: PendingBindingKind, op: Omit<PendingBindingOp, 'key' | 'kind'>): number {
    return ops.value.findIndex(
      (o) =>
        o.kind === kind &&
        o.list_group_id === op.list_group_id &&
        o.table_field_id === op.table_field_id &&
        o.child_object_list_id === op.child_object_list_id,
    )
  }

  function removeAt(idx: number): void {
    if (idx < 0) return
    ops.value = ops.value.filter((_, i) => i !== idx)
  }

  /** Привязка: если уже есть отложенная отвязка того же ребёнка — они взаимно гасятся. */
  function stageLink(op: Omit<PendingBindingOp, 'key' | 'kind'>): void {
    const cancelIdx = findIndex('unlink', op)
    if (cancelIdx >= 0) {
      removeAt(cancelIdx)
      return
    }
    if (findIndex('link', op) >= 0) return
    ops.value = [
      ...ops.value,
      { ...op, kind: 'link', key: opKey('link', op.list_group_id, op.table_field_id, op.child_object_list_id) },
    ]
  }

  /** Отвязка: если уже есть отложенная привязка того же ребёнка — отменяем её. */
  function stageUnlink(op: Omit<PendingBindingOp, 'key' | 'kind'>): void {
    const cancelIdx = findIndex('link', op)
    if (cancelIdx >= 0) {
      removeAt(cancelIdx)
      return
    }
    if (findIndex('unlink', op) >= 0) return
    ops.value = [
      ...ops.value,
      { ...op, kind: 'unlink', key: opKey('unlink', op.list_group_id, op.table_field_id, op.child_object_list_id) },
    ]
  }

  function undo(key: string): void {
    ops.value = ops.value.filter((o) => o.key !== key)
  }

  function clear(): void {
    ops.value = []
  }

  function pendingForTable(listGroupId: number, tableFieldId: string): PendingForTable {
    const added: PendingBindingOp[] = []
    const removed: PendingBindingOp[] = []
    for (const o of ops.value) {
      if (o.list_group_id !== listGroupId || o.table_field_id !== tableFieldId) continue
      if (o.kind === 'link') added.push(o)
      else removed.push(o)
    }
    return { added, removed }
  }

  function buildDelta(): TableBindingsDelta | null {
    if (ops.value.length === 0) return null
    return {
      link: ops.value.filter((o) => o.kind === 'link').map(toBody),
      unlink: ops.value.filter((o) => o.kind === 'unlink').map(toBody),
    }
  }

  return {
    ops,
    hasPending,
    stageLink,
    stageUnlink,
    undo,
    clear,
    pendingForTable,
    buildDelta,
  }
}

const NOOP_BINDINGS: PendingTableBindings = {
  ops: shallowRef([]),
  hasPending: computed(() => false),
  stageLink: () => {},
  stageUnlink: () => {},
  undo: () => {},
  clear: () => {},
  pendingForTable: () => ({ added: [], removed: [] }),
  buildDelta: () => null,
}

/** inject с no-op fallback для контекстов без модели паспорта (Create V2 batch, headerOnly). */
export function usePendingTableBindingsOptional(): PendingTableBindings {
  return inject(PENDING_BINDINGS_KEY, NOOP_BINDINGS)
}
